import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Pause, RotateCcw, RotateCw, Volume2, VolumeX } from 'lucide-react';
import type { MediaPlayer } from '@/presentation/hook/useMediaPlayer';
import { mediaRangeFillStyle } from '@/presentation/util/mediaRange';
import {
  mediaControlButtonClass,
  mediaRangeClass,
} from '@/presentation/component/course/mediaPlayerStyles';
import { PlaybackRateMenu } from '@/presentation/component/course/PlaybackRateMenu';
import { SKIP_SECONDS } from '@/presentation/util/mediaSkip';

const formatTime = (time: number) => {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

interface MediaPlayerControlsProps {
  player: MediaPlayer<HTMLMediaElement>;
  children?: ReactNode;
}

export const MediaPlayerControls = ({ player, children }: MediaPlayerControlsProps) => {
  const { t } = useTranslation();
  const { isPlaying, currentTime, duration, volume, isMuted, playbackRate } = player;
  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const volumePercent = isMuted ? 0 : volume * 100;

  return (
    <>
      <div className="mb-1">
        <input
          type="range"
          min="0"
          max="100"
          value={progressPercent}
          onChange={(e) => player.seekToPercent(parseFloat(e.target.value))}
          className={`w-full ${mediaRangeClass}`}
          style={mediaRangeFillStyle(progressPercent)}
          aria-label={t('mediaPlayer.seek')}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <div className="flex items-center gap-1 sm:gap-3">
          <button
            onClick={player.skipBackward}
            className={mediaControlButtonClass}
            aria-label={t('mediaPlayer.skipBackward', { seconds: SKIP_SECONDS })}
          >
            <RotateCcw className="w-5 h-5" />
          </button>

          <button
            onClick={player.togglePlay}
            className={mediaControlButtonClass}
            aria-label={isPlaying ? t('mediaPlayer.pause') : t('mediaPlayer.play')}
          >
            {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
          </button>

          <button
            onClick={player.skipForward}
            className={mediaControlButtonClass}
            aria-label={t('mediaPlayer.skipForward', { seconds: SKIP_SECONDS })}
          >
            <RotateCw className="w-5 h-5" />
          </button>

          <span className="text-sm tabular-nums">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>

        <div className="flex items-center gap-1 sm:gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={player.toggleMute}
              className={mediaControlButtonClass}
              aria-label={isMuted ? t('mediaPlayer.unmute') : t('mediaPlayer.mute')}
            >
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
            <input
              type="range"
              min="0"
              max="100"
              value={volumePercent}
              onChange={(e) => player.changeVolume(parseFloat(e.target.value))}
              className={`hidden sm:block w-20 ${mediaRangeClass}`}
              style={mediaRangeFillStyle(volumePercent)}
              aria-label={t('mediaPlayer.volume')}
            />
          </div>

          <PlaybackRateMenu rate={playbackRate} onChange={player.changePlaybackRate} />

          {children}
        </div>
      </div>
    </>
  );
};
